/**
 * Pure helpers for camera-facing logic.
 *
 * These are deliberately free of browser APIs, React, and zustand so they can
 * be unit-tested without any environment setup. All browser-bound work
 * (getUserMedia, enumerateDevices, replaceTrack) lives in RoomClient.
 */

export type CameraFacing = 'user' | 'environment'

/**
 * Returns the opposite camera facing.
 *   'user' (front) → 'environment' (back)
 *   'environment' (back) → 'user' (front)
 */
export function nextFacing(current: CameraFacing): CameraFacing {
  return current === 'user' ? 'environment' : 'user'
}

/**
 * Whether the switch-camera control should be shown.
 * Requires at least 2 video-input devices AND the camera to be currently on.
 * (FR-002 / C1 / C2 / C3)
 */
export function canSwitchCamera(videoInputCount: number, camOn: boolean): boolean {
  return videoInputCount >= 2 && camOn
}

/**
 * Whether the local self-view should be mirrored.
 * The front camera ('user') is mirrored (matching the "mirror selfie" convention);
 * the back camera ('environment') is not. (FR-008 / C9)
 */
export function isMirrored(facing: CameraFacing): boolean {
  return facing === 'user'
}
